'use client';
import { useCallback, useState } from 'react';
import { useWallets } from '@privy-io/react-auth';
import { useQueryClient } from '@tanstack/react-query';
import { createWalletClient, custom } from 'viem';
import { polygon } from 'viem/chains';

import { useMixpanel } from '@/hooks/useMixpanel';
import { walletApi } from '@/services/api/wallet.api';
import { useConfirmWithdrawal, useExecuteWithdrawalRelay, withdrawalKeys } from '@/services/query';
import { useAuth } from '@/store';
import type { DepositWalletTypedData, PrepareResponse } from '@/types/withdrawal.types';

export type OfframpStep =
  | 'idle'
  | 'loading_tx'
  | 'awaiting_sign'
  | 'confirming'
  | 'completed'
  | 'failed';

export interface UseOfframpReturn {
  step: OfframpStep;
  pendingTx: PrepareResponse | null;
  txHash: string | null;
  error: string | null;
  completeOfframp: (withdrawalId: string, ownerAddress?: string) => Promise<void>;
  completeWalletOutflow: (amount: number, destinationAddress: string) => Promise<void>;
  reset: () => void;
  wallets: any[];
  isPending: boolean;
}

/**
 * High-level coordinator hook for the Polymarket off-ramp flow.
 */
export function useOfframp(): UseOfframpReturn {
  const { wallets } = useWallets();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<OfframpStep>('idle');
  const [pendingTx, setPendingTx] = useState<PrepareResponse | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mutations for state-aware API interactions
  const relayMutation = useExecuteWithdrawalRelay(accessToken ?? '');
  const confirmMutation = useConfirmWithdrawal();
  const { trackWithdrawalStarted, trackWithdrawalCompleted, trackWithdrawalFailed } = useMixpanel();

  const reset = useCallback(() => {
    setStep('idle');
    setPendingTx(null);
    setTxHash(null);
    setError(null);
    relayMutation.reset();
    confirmMutation.reset();
  }, [relayMutation, confirmMutation]);

  /**
   * Orchestrates the multi-step withdrawal signing process:
   * 1. Resolve embedded wallet from Privy
   * 2. Fetch EIP-712 payload from backend
   * 3. Trigger manual chain switch and signature popup
   * 4. Submit to Relayer
   * 5. Final backend confirmation
   */
  const completeOfframp = useCallback(
    async (withdrawalId: string, ownerAddress?: string) => {
      try {
        setError(null);
        setStep('loading_tx');

        // -- 1. Resolve connected wallet --
        const signingWallet =
          wallets.find((w) => w.walletClientType === 'privy' || w.connectorType === 'embedded') ||
          wallets[0];
        if (!signingWallet) {
          throw new Error('Connected wallet not found. Please log in.');
        }

        const resolvedOwnerAddress = ownerAddress || signingWallet.address;

        // -- 2. Fetch Transaction Payload --
        // Using queryClient.fetchQuery to ensure it integrates with TanStack cache
        const prepared = await queryClient.fetchQuery({
          queryKey: withdrawalKeys.payload(withdrawalId, resolvedOwnerAddress),
          queryFn: () => walletApi.fetchWithdrawalPayload(withdrawalId, resolvedOwnerAddress),
        });

        if (!prepared || !prepared.typedData) {
          throw new Error('Backend returned successfully but transaction payload is missing.');
        }

        setPendingTx(prepared);
        trackWithdrawalStarted({
          amount: Number(prepared.meta?.inAmount || 0),
          currency: prepared.meta?.sourceCurrency || 'USDC',
          destination_type: 'bank',
        });

        setStep('awaiting_sign');
        await signingWallet.switchChain(137);
        const ethereumProvider = await signingWallet.getEthereumProvider();
        const walletClient = createWalletClient({
          account: signingWallet.address as `0x${string}`,
          chain: polygon,
          transport: custom(ethereumProvider),
        });

        const isDepositWallet =
          prepared.walletType === 'deposit_wallet' || prepared.typedData?.primaryType === 'Batch';

        let finalTxHash: string;

        if (isDepositWallet) {
          // -- Deposit wallet path: sign Batch EIP-712 → backend submits via HMAC --
          const td = prepared.typedData as DepositWalletTypedData;
          const batchMessage = {
            wallet: td.message.wallet as `0x${string}`,
            nonce: BigInt(String(td.message.nonce)),
            deadline: BigInt(String(td.message.deadline)),
            calls: td.message.calls.map((c) => ({
              target: c.target as `0x${string}`,
              value: BigInt(String(c.value)),
              data: c.data as `0x${string}`,
            })),
          };

          const signature = await walletClient.signTypedData({
            account: signingWallet.address as `0x${string}`,
            domain: {
              name: td.domain.name,
              version: td.domain.version,
              chainId: BigInt(td.domain.chainId),
              verifyingContract: td.domain.verifyingContract as `0x${string}`,
            },
            types: {
              Call: [...td.types.Call],
              Batch: [...td.types.Batch],
            },
            primaryType: 'Batch',
            message: batchMessage,
          });

          setStep('confirming');
          const submitRes = await walletApi.submitBankWithdrawal(
            withdrawalId,
            signature,
            prepared.meta as Record<string, unknown>,
          );
          finalTxHash = submitRes?.transactionHash ?? '';
        } else {
          // -- Legacy gnosis_safe path: client-side relay → then backend confirm --
          const relayResult = await relayMutation.mutateAsync({
            walletClient,
            typedData: prepared.typedData,
          });
          setStep('confirming');
          const submitRes = await confirmMutation.mutateAsync({
            withdrawalId,
            transactionHash: relayResult.transactionHash,
            ownerAddress: resolvedOwnerAddress,
          });
          finalTxHash = submitRes?.data?.transactionHash || relayResult.transactionHash;
        }

        setTxHash(finalTxHash);
        setStep('completed');
        trackWithdrawalCompleted({
          amount: Number(prepared.meta?.inAmount || 0),
          currency: prepared.meta?.sourceCurrency || 'USDC',
          fee: 0,
          tx_hash: finalTxHash,
        });
      } catch (err: any) {
        const message =
          err.message || 'Finalization failed. Please check your wallet and try again.';
        console.error('Error in offramp flow:', message);
        setError(message);
        setStep('failed');
        trackWithdrawalFailed({
          amount: 0, // difficult to get exact amount from out of scope error unless using ref
          error_code: message,
        });
      }
    },
    [wallets, queryClient, relayMutation, confirmMutation],
  );

  const completeWalletOutflow = useCallback(
    async (amount: number, destinationAddress: string) => {
      try {
        setError(null);
        setStep('loading_tx');

        const signingWallet =
          wallets.find((w) => w.walletClientType === 'privy' || w.connectorType === 'embedded') ||
          wallets[0];
        if (!signingWallet) {
          throw new Error('Connected wallet not found. Please log in.');
        }

        const prepared = await walletApi.fetchWalletWithdrawalPayload(
          amount,
          destinationAddress,
          signingWallet.address,
        );

        if (!prepared || !prepared.typedData) {
          throw new Error('Backend returned successfully but transaction payload is missing.');
        }

        setPendingTx(prepared);
        trackWithdrawalStarted({
          amount,
          currency: 'USDC',
          destination_type: 'wallet',
        });

        setStep('awaiting_sign');
        await signingWallet.switchChain(137);
        const ethereumProvider = await signingWallet.getEthereumProvider();
        const walletClient = createWalletClient({
          account: signingWallet.address as `0x${string}`,
          chain: polygon,
          transport: custom(ethereumProvider),
        });

        const relayResult = await relayMutation.mutateAsync({
          walletClient,
          typedData: prepared.typedData,
        });

        setStep('confirming');
        const submitRes = await walletApi.confirmWalletWithdrawal(
          String(prepared.withdrawalId),
          relayResult.transactionHash,
        );

        setTxHash(submitRes?.data?.transactionHash || relayResult.transactionHash);
        setStep('completed');
        trackWithdrawalCompleted({
          amount,
          currency: 'USDC',
          fee: 0,
          tx_hash: submitRes?.data?.transactionHash || relayResult.transactionHash,
        });
      } catch (err: any) {
        const message =
          err.message || 'Wallet withdrawal failed. Please check your wallet and try again.';
        console.error('Error in wallet withdrawal flow:', message);
        setError(message);
        setStep('failed');
        trackWithdrawalFailed({
          amount: 0,
          error_code: message,
        });
      }
    },
    [
      wallets,
      relayMutation,
      trackWithdrawalStarted,
      trackWithdrawalCompleted,
      trackWithdrawalFailed,
    ],
  );

  return {
    step,
    pendingTx,
    txHash,
    error,
    completeOfframp,
    completeWalletOutflow,
    reset,
    wallets,
    isPending: relayMutation.isPending || confirmMutation.isPending,
  };
}
